import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';

import { NotificationContext } from '@client/context/NotificationContext';
import { useDirty } from '@client/store/dirty.store';
import { useLoading } from '@client/store/loading.store';

import type { HookAPI } from 'antd/es/modal/useModal';

type TModalRef = ReturnType<HookAPI['confirm']> | null;

/**
 * Custom hook to handle unsaved changes prompt.
 */
export function usePrompt() {
  const { t } = useTranslation();
  const { isDirty } = useDirty();
  const { loading } = useLoading();
  const { modalApi } = useContext(NotificationContext);

  // Use a callback for useBlocker to ensure it always uses the latest state
  // and check if navigation is actually to a different path. Search-param
  // updates within the same path (e.g. table pagination writing back to URL)
  // must not trigger the unsaved-changes prompt.
  const blocker = useBlocker(({ nextLocation }) => {
    const isSamePath =
      nextLocation.pathname === window.location.pathname &&
      nextLocation.hash === window.location.hash;

    return isDirty && !loading && !isSamePath;
  });

  // `useBlocker` returns a fresh object on every render. Holding the latest
  // value in a ref keeps modal-management effects from re-running just because
  // the wrapper identity changed, which previously destroyed and re-created
  // the antd modal on every render and caused a focus-sync recursion
  // ("Maximum call stack size exceeded" inside antd's `syncFocus`).
  const blockerRef = useRef(blocker);
  useEffect(() => {
    blockerRef.current = blocker;
  });

  const [isModalVisible, setIsModalVisible] = useState(false);

  const modalRef = useRef<TModalRef>(null);

  // Block tab/browser close
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty && !loading) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, loading]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setIsModalVisible(true);
    } else if (blocker.state === 'unblocked') {
      setIsModalVisible(false);
    }
  }, [blocker.state]);

  // Open or close the modal in response to `isModalVisible` only. Reading the
  // blocker through `blockerRef` keeps this effect off the blocker's identity.
  useEffect(() => {
    if (!isModalVisible) {
      if (modalRef.current) {
        modalRef.current.destroy();
        modalRef.current = null;
      }
      return;
    }

    if (modalRef.current) return;

    // `modalApi` comes from `Modal.useModal()` in `mainLayout` and respects
    // the active ConfigProvider context. Falling back to the static
    // `Modal.confirm` here would emit the antd warning about static functions
    // being unable to consume dynamic context.
    if (!modalApi) return;

    modalRef.current = modalApi.confirm({
      title: t('message.unload.title'),
      content: t('message.unload.description'),
      okText: t('actions.leave'),
      cancelText: t('actions.stay'),
      onOk: () => {
        blockerRef.current?.proceed?.();
        setIsModalVisible(false);
        modalRef.current = null;
      },
      onCancel: () => {
        setIsModalVisible(false);
        blockerRef.current?.reset?.();
        modalRef.current = null;
      },
    });
  }, [isModalVisible, modalApi, t]);

  // Destroy any lingering modal on unmount only.
  useEffect(
    () => () => {
      if (modalRef.current) {
        modalRef.current.destroy();
        modalRef.current = null;
      }
    },
    [],
  );
}
